import axios, { AxiosResponse } from "axios";
import { ValidationError } from "yup";

import { DomesticTypes, SamskipAPIResponse } from "@assets/types";

import { priceResultSchema } from "../schemas";
import {
  DomesticPriceAddress,
  PriceResult,
  CalculatePriceRequest,
  PostCodeDTO,
} from "../types";

const SamskipAPI = process.env.SamskipAPI;
const isDevelopmentEnvironment =
  (process.env.GATSBY_ACTIVE_ENV || process.env.NODE_ENV) === "development";

export const getLocations = async (): Promise<DomesticPriceAddress[]> => {
  const res: AxiosResponse<SamskipAPIResponse<PostCodeDTO[]>> = await axios.get(
    `${SamskipAPI}/Domestic/postcodes`,
    {
      withCredentials: true,
    }
  );

  const postCodes = res.data.ReturnItem.Data;

  const locationsMap = postCodes.reduce((acc, p) => {
    const city = p.City;

    if (!acc[city]) {
      acc[city] = {
        value: city,
        label: city,
        defaultAddress: p.DefaultAddress,
      };
    }

    return acc;
  }, {} as Record<string, DomesticPriceAddress>);

  return Object.values(locationsMap).sort((a, b) =>
    a.label.localeCompare(b.label, "is")
  );
};

export const calculatePrice = async (
  request: CalculatePriceRequest
): Promise<PriceResult> => {
  const res: AxiosResponse<PriceResult> = await axios.post(
    `${SamskipAPI}/Domestic/bookings/quotes/calculate-price`,
    {
      Departure: request.Departure,
      Destination: request.Destination,
      Category: "P00",
      Weight: request.Weight,
      Volume: request.Volume,
      Amount: 1,
      IsIMO: request.IsIMO,
      IsFragile: request.IsFragile,
      IsFrozen: request.IsFrozen,
      UseCompany: request.UseCompany,
    },
    { withCredentials: true }
  );

  try {
    return await priceResultSchema.validate(res.data, {
      abortEarly: !isDevelopmentEnvironment,
    });
  } catch (err) {
    if (isDevelopmentEnvironment && err instanceof ValidationError) {
      err.inner.forEach((e) => console.error(e.message, e.path));
    }
    throw err;
  }
};

export const searchCompanyCustomers = async (companySearchString: string) => {
  const res: AxiosResponse<
    SamskipAPIResponse<DomesticTypes.DomesticCustomer[]>
  > = await axios.get(`${SamskipAPI}/domestic/customers/companies/search`, {
    params: {
      searchString: companySearchString,
    },
    withCredentials: true,
  });
  return res.data.ReturnItem.Data;
};
